import React, { useEffect, useMemo } from 'react';
import { groupBy, forEach, find } from 'lodash';
import {
	AccountSwitcher,
	Box,
	BDSProvider,
	AppBar,
} from '@barracuda-internal/bds-core-v2';
import AddAccount from './AddAccountPopup';
import BCCAccountToolbar from 'src/components/BCC/BCCAccountToolbar';
import TierStatusRow from './TierStatusRow';
import useAuth from 'src/hooks/useAuth';
import { useAppToolchain } from 'src/hooks/useAppToolchain';
import useStyles from 'src/styles/di-theme';
import withErrorBoundary from './WithErrorBoundary';
import jwtDecode from 'jwt-decode';

export function Header(): React.ReactElement {
	const { api } = useAppToolchain();
	const { auth } = useAuth();

	const currentAccount = auth.accountDetails.account;
	const classes = useStyles();
	const [nestedAccounts, setNestedAccounts] = React.useState([]);
	const [selectedAccount, setSelectedAccount] = React.useState<any>(null);
	const [currentCompany, setCurrentCompany] = React.useState<any>(null);
	const [showAddAccount, setShowAddAccount] = React.useState<boolean>(false);

	const setAcc = (accounts) => {
		const bccAccounts =
			accounts.map((a) => ({
				id: a.account.id,
				name: a.account.name == null ? '(No Name)' : a.account.name,
				companyId:
					a.account.companyId == null ? '0' : a.account.companyId,
				companyName:
					a.account.company == null
						? '(No Name)'
						: a.account.company.name == null
						? '(No Name)'
						: a.account.company.name,
			})) || [];
		const groupByCompany = groupBy(bccAccounts, 'companyId');
		const accountsWithSubAccounts = [];
		forEach(groupByCompany, (group, companyId) => {
			if (companyId == '0') {
				accountsWithSubAccounts.push({
					id: companyId,
					name: 'Unassigned Tenants',
					nestedAccounts: group,
				});
			} else {
				accountsWithSubAccounts.push({
					id: companyId,
					name: group[0].companyName,
					nestedAccounts: group,
				});
			}
		});
		setNestedAccounts(accountsWithSubAccounts);

		setSelectedAccount(find(bccAccounts, { id: currentAccount.id }));
	};

	useEffect(() => {
		setAcc(auth.accounts);
		if (currentAccount.companyId != null) {
			setCurrentCompany(currentAccount.company);
		} else {
			setCurrentCompany({ id: '0', name: 'Unassigned Tenants' });
		}
	}, []);

	const handleAccountChange = (newAccountId: string) => {
		if (newAccountId === currentAccount.id) return;

		const newAccount = auth.accounts.find(
			(a) => a.account.id === newAccountId,
		);

		localStorage.setItem('isNewAccountSwitched', 'true');
		console.info('changing', newAccountId, newAccount);
		sessionStorage.clear();
		localStorage.setItem('lastActiveAccount', newAccountId);
		localStorage.removeItem('is_setup');
		location.href = '/';
	};

	const fetchAccounts = async () => {
		try {
			const accounts = await api.getUserAccounts(auth.user.id);
			setAcc(accounts);
		} catch (error) {
			console.error('Failed to fetch accounts:', error);
		}
	};

	const checkIfMspPartner = useMemo(() => {
		const tenantId = Number(currentAccount.company.tenantId);
		try {
			const oidc_access_token_decoded = jwtDecode(
				auth.oidcUser.access_token,
			);
			const bcc_data = JSON.parse(
				oidc_access_token_decoded[
					'http://schemas.barracuda.com/datainspector/bcc_msp_account'
				],
			);
			const account = bcc_data.find((item) => item === tenantId);
			return account ? true : false;
		} catch (error) {
			console.error('Error decoding token or parsing data:', error);
			return false;
		}
	}, [auth]);

	return (
		<BDSProvider>
			<AppBar
				position="static"
				color="inherit"
				elevation={0}
				style={{ position: 'relative', zIndex: 10 }}
			>
				{auth ? (
					<Box>
						{selectedAccount != null ? (
							<AddAccount
								companyId={currentCompany.id}
								companyName={currentCompany.name}
								showAdd={showAddAccount}
								closePopup={(refresh) => {
									setShowAddAccount(false);
									if (refresh) {
										fetchAccounts();
									}
								}}
							></AddAccount>
						) : null}

						<BCCAccountToolbar>
							{selectedAccount != null ? (
								<Box>
									<AccountSwitcher
										accounts={nestedAccounts}
										onSelectAccountCallback={(acc) => {
											handleAccountChange(acc.id);
										}}
										manageAccountText={
											auth.isFullAdmin()
												? '+ Add Tenant'
												: ''
										}
										onManageAccountClick={() => {
											setShowAddAccount(true);
										}}
										addMSTenantText={
											checkIfMspPartner
												? 'Manage accounts'
												: null
										}
										onAddMSTenantClick={() => {
											const msp_url =
												'https://manage.msp.barracudanetworks.com/';
											window.open(msp_url, '_blank');
										}}
										initAccount={selectedAccount.id}
									/>{' '}
								</Box>
							) : null}
						</BCCAccountToolbar>
						{window.location.pathname !== '/welcome' ? (
							<Box className={classes['mt_4']}>
								{' '}
								<TierStatusRow></TierStatusRow>{' '}
							</Box>
						) : null}
					</Box>
				) : null}
			</AppBar>
		</BDSProvider>
	);
}
export default withErrorBoundary(Header);
